import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Users,
  CheckCircle,
  Zap,
  ArrowRight,
  Plus,
  Play,
  Pause,
  Settings,
  AlertCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { chatbotService } from '../../../services/chatbotService';
import { subscriptionService } from '../../../services/subscriptionService';
import { billingService } from '../../../services/billingService';
import { authService } from '../../../services/authService';
import { useDateFormat } from '../../../hooks/useDateFormat';

const ChatbotDashboard = () => {
  const { t } = useTranslation();
  const { formatDate, formatDateTime, formatDateOnly } = useDateFormat();

  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chatbots, setChatbots] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatbotSub, setChatbotSub] = useState(null);
  const [activeBlock, setActiveBlock] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    subscriptionService.getChatbotSubscription()
      .then(data => setChatbotSub(data))
      .catch(() => {});
    const companyId = authService.getCompanyId();
    if (companyId) {
      billingService.getActiveChatbotBlock(companyId)
        .then(data => setActiveBlock(data))
        .catch(() => {});
    }
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, chatbotsData, conversationsData] = await Promise.all([
        chatbotService.getDashboardStats(),
        chatbotService.getChatbots(),
        chatbotService.getConversations(null, 0, 5),
      ]);
      setStats(statsData);
      setChatbots(chatbotsData || []);
      setConversations(conversationsData?.content || []);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (chatbot) => {
    try {
      await chatbotService.updateChatbot(chatbot.id, { isActive: !chatbot.isActive });
      fetchDashboardData();
    } catch (err) {
      console.error('Failed to toggle chatbot:', err);
    }
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: t('common.active') };
    }
    return { color: 'bg-yellow-100 text-yellow-800', icon: Pause, label: t('chatbot.cb_paused') };
  };

  const getConversationStatusBadge = (isActive) => {
    if (isActive) {
      return { color: 'bg-blue-100 text-blue-800', icon: MessageSquare, label: t('common.active') };
    }
    return { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Ended' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('common.error')}</h2>
        <button onClick={fetchDashboardData} className="text-primary-600 hover:underline">
          Try again
        </button>
      </div>
    );
  }

  const statCards = [
    {
      name: t('chatbot.totalChatbots'),
      value: stats?.totalChatbots || 0,
      icon: MessageSquare,
      color: 'bg-blue-500',
    },
    {
      name: t('chatbot.activeChatbots'),
      value: stats?.activeChatbots || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
    },
    {
      name: t('chatbot.totalConversations'),
      value: stats?.totalConversations || 0,
      icon: Users,
      color: 'bg-purple-500',
    },
    {
      name: t('chatbot.totalMessages'),
      value: stats?.totalMessages || 0,
      icon: Zap,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('chatbot.dashboard')}</h1>
          <p className="text-gray-600 mt-1">{t('chatbot.cb_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/portal/chatbot/conversations" className="btn btn-outline">
            <MessageSquare className="w-5 h-5 mr-2" />
            {t('chatbot.conversations')}
          </Link>
          <Link to="/portal/chatbot/manage" className="btn btn-primary">
            <Plus className="w-5 h-5 mr-2" />
            {t('chatbot.cb_addChatbot')}
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
              </div>
              <div className="text-sm text-gray-600">{stat.name}</div>
            </div>
          );
        })}
      </div>

      {chatbotSub && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">{t('chatbot.cb_messageUsage')}</h3>

          {/* Plan usage bar — same style as Billing page */}
          {(() => {
            const current = chatbotSub.messagesUsedThisMonth ?? 0;
            const limit = chatbotSub.messagesPerMonth ?? 0;
            const percentage = limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
            const atLimit = limit > 0 && current >= limit;
            return (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700">{t('chatbot.cb_messagesThisMonth')}</span>
                  <span className="text-gray-600">{current} / {limit}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${atLimit ? 'bg-red-500' : 'bg-primary-600'}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })()}

          {/* Active block card — same as Billing page */}
          {activeBlock && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-purple-900">{activeBlock.blockName}</span>
                <span className="text-sm text-purple-700">
                  Expires {formatDateOnly(activeBlock.expiresAt)}
                </span>
              </div>
              <div className="w-full bg-purple-100 rounded-full h-2 overflow-hidden mb-1">
                <div
                  className="bg-purple-600 h-2 rounded-full"
                  style={{
                    width: `${Math.min(100, (activeBlock.messagesUsed / activeBlock.messagesTotal) * 100)}%`
                  }}
                />
              </div>
              <div className="flex justify-between text-sm text-purple-700">
                <span>{activeBlock.messagesUsed.toLocaleString()} used</span>
                <span>{activeBlock.messagesRemaining.toLocaleString()} remaining</span>
              </div>
            </div>
          )}

          {/* Overage info */}
          {chatbotSub.overageEnabled && chatbotSub.overageMessagesUsed > 0 && (
            <div className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              <span className="font-medium">{chatbotSub.overageMessagesUsed} overage messages</span>
              {chatbotSub.overagePricePerMessage && (
                <span className="ml-1">
                  (${(chatbotSub.overageMessagesUsed * chatbotSub.overagePricePerMessage).toFixed(2)} est.)
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {chatbotSub && (() => {
        const used = chatbotSub.messagesUsedThisMonth ?? 0;
        const limit = chatbotSub.messagesPerMonth;
        const overageEnabled = chatbotSub.overageEnabled;
        const isTrial = chatbotSub.status === 'TRIAL';
        const isBlocked = limit !== null && used >= limit && !overageEnabled;

        if (!isBlocked) return null;

        return (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center space-y-3">
            <p className="text-red-800 font-semibold">
              {isTrial
                ? `Trial limit reached. You've used all ${limit} free messages.`
                : 'Monthly message limit reached. Upgrade your plan or enable overage to continue.'}
            </p>
            <button
              onClick={() => navigate('/portal/billing?upgrade=chatbot')}
              className="px-5 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            >
              Upgrade Now
            </button>
          </div>
        );
      })()}

      {/* Active Chatbots */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">{t('chatbot.cb_yourChatbots')}</h2>
          <Link to="/portal/chatbot/manage" className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1">
            {t('chatbot.manage')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {chatbots.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No chatbots yet. Create your first chatbot to get started.</p>
            <Link to="/portal/chatbot/manage" className="btn btn-primary">
              <Plus className="w-5 h-5 mr-2" />
              {t('chatbot.cb_addChatbot')}
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {chatbots.map((bot) => {
              const statusBadge = getStatusBadge(bot.isActive);
              const StatusIcon = statusBadge.icon;
              return (
                <div key={bot.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-gray-900">{bot.name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusBadge.label}
                        </span>
                      </div>
                      {bot.description && (
                        <p className="text-sm text-gray-500 line-clamp-1">{bot.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Link to="/portal/chatbot/manage" className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg">
                        <Settings className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleToggleActive(bot)}
                        className={`p-2 rounded-lg ${bot.isActive
                          ? 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                          : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {bot.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">{t('chatbot.conversations')}</div>
                      <div className="font-bold text-gray-900">{(bot.totalConversations || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">{t('chatbot.cb_messages')}</div>
                      <div className="font-bold text-gray-900">{(bot.totalMessages || 0).toLocaleString()}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    {bot.primaryColor && (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: bot.primaryColor }}></div>
                        <span className="text-xs text-gray-500">Theme</span>
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      {t('chatbot.cb_createdAt')} {bot.createdAt ? formatDate(bot.createdAt) : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Conversations */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">{t('chatbot.conversations')}</h2>
          <Link to="/portal/chatbot/conversations" className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-1">
            {t('common.viewAll')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {conversations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>{t('chatbot.cb_noConversations')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {conversations.map((conv) => {
              const statusBadge = getConversationStatusBadge(conv.isActive);
              const StatusIcon = statusBadge.icon;
              return (
                <div key={conv.id} className="p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">
                          {conv.visitorName || conv.visitorEmail || 'Anonymous'}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusBadge.label}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">{conv.chatbotName}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">
                        {conv.createdAt ? formatDateTime(conv.createdAt) : ''}
                      </div>
                      <div className="text-xs font-medium text-gray-700 mt-1">
                        {conv.messageCount || 0} messages
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Performance Overview */}
      <div className="bg-gradient-to-r from-primary-600 to-blue-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-6">{t('chatbot.cb_quickStats')}</h2>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold mb-1">{chatbots.length}</div>
            <div className="text-sm opacity-80">{t('chatbot.totalChatbots')}</div>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold mb-1">{chatbots.filter(b => b.isActive).length}</div>
            <div className="text-sm opacity-80">{t('chatbot.activeChatbots')}</div>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold mb-1">{(stats?.totalConversations || 0).toLocaleString()}</div>
            <div className="text-sm opacity-80">{t('chatbot.totalConversations')}</div>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Zap className="w-6 h-6" />
            </div>
            <div className="text-3xl font-bold mb-1">{(stats?.totalMessages || 0).toLocaleString()}</div>
            <div className="text-sm opacity-80">{t('chatbot.totalMessages')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotDashboard;
